import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaCheckCircle, FaTimes, FaDownload,
  FaFilePdf, FaImage, FaInfoCircle, FaSave
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function AdminVerifyUpload({ onLogout }) {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upload, setUpload] = useState(null);
  const [verifiedVolume, setVerifiedVolume] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchUpload();
  }, [uploadId]);

  const fetchUpload = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/subbie/uploads/verify/${uploadId}`);
      const data = await response.json();
      if (data.success) {
        setUpload(data.upload);
        setVerifiedVolume(data.upload.volume_mt || '');
        setVerificationNotes(data.upload.notes || '');
      } else {
        toast.error(data.error || 'Failed to load upload');
      }
    } catch (err) {
      console.error('Error fetching upload:', err);
      toast.error('Failed to load upload');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (status) => {
    setProcessing(true);
    try {
      const response = await fetch(`/subbie/uploads/${uploadId}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_id: user.id,
          verified_volume_mt: verifiedVolume,
          verification_notes: verificationNotes,
          status: status
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Upload ${status} successfully!`);
        navigate('/admin/uploads');
      } else {
        toast.error(data.error || 'Failed to verify upload');
      }
    } catch (err) {
      console.error('Error verifying upload:', err);
      toast.error('Failed to verify upload');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeTab="uploads" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading upload...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!upload) {
    return (
      <AdminLayout activeTab="uploads" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">📄</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Upload Not Found</h2>
          <p className="text-gray-500">The upload you're looking for doesn't exist.</p>
        </div>
      </AdminLayout>
    );
  }

  const getTypeLabel = (type) => {
    const labels = {
      'pod': '📄 POD (Proof of Delivery)',
      'weighbridge': '⚖️ Weighbridge Ticket',
      'fuel': '⛽ Fuel Receipt',
      'check_sheet': '📋 Check Sheet',
      'driver_hours': '🕐 Driver Hours'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'verified': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  return (
    <AdminLayout activeTab="uploads" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/admin/uploads')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: '#0A1929' }}
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📄 Verify Upload</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            Review and verify the uploaded document
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        {/* Upload Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-xs" style={{ color: '#94A3B8' }}>Upload Type</p>
            <p className="font-medium" style={{ color: '#0A1929' }}>{getTypeLabel(upload.upload_type)}</p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#94A3B8' }}>Job Number</p>
            <p className="font-medium" style={{ color: '#0A1929' }}>{upload.jobs?.job_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#94A3B8' }}>Subcontractor</p>
            <p className="font-medium" style={{ color: '#0A1929' }}>{upload.subcontractors?.company_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#94A3B8' }}>Status</p>
            <span className={getStatusBadge(upload.status)}>{upload.status || 'pending'}</span>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#94A3B8' }}>Uploaded</p>
            <p className="font-medium" style={{ color: '#0A1929' }}>
              {upload.created_at ? new Date(upload.created_at).toLocaleString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#94A3B8' }}>Submitted Volume</p>
            <p className="font-medium" style={{ color: '#0A1929' }}>{upload.volume_mt || 0} MT</p>
          </div>
        </div>

        {/* Notes */}
        {upload.notes && (
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs" style={{ color: '#94A3B8' }}>Subbie Notes</p>
            <p className="text-sm" style={{ color: '#4A5568' }}>{upload.notes}</p>
          </div>
        )}

        {/* File Preview */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-dashed" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            {upload.file_url?.endsWith('.pdf') ? (
              <FaFilePdf className="text-3xl text-red-500" />
            ) : (
              <FaImage className="text-3xl text-blue-500" />
            )}
            <div>
              <p className="font-medium" style={{ color: '#0A1929' }}>{upload.file_name || 'Uploaded File'}</p>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                {upload.file_size ? `${(upload.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
              </p>
            </div>
            {upload.file_url && (
              <button className="ml-auto px-3 py-1 border rounded-lg text-sm hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center gap-2" style={{ borderColor: '#E8ECEF' }}>
                <FaDownload size={14} /> Download
              </button>
            )}
          </div>
        </div>

        {/* Verification Section */}
        <div className="border-t pt-4" style={{ borderColor: '#E8ECEF' }}>
          <h3 className="font-bold mb-4" style={{ color: '#0A1929' }}>Verify Upload</h3>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
              Verified Volume (MT) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={verifiedVolume}
              onChange={(e) => setVerifiedVolume(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
              placeholder="Enter verified volume"
              required
            />
            <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
              <FaInfoCircle className="inline mr-1" size={12} />
              Subbie submitted: {upload.volume_mt || 0} MT
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
              Verification Notes
            </label>
            <textarea
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
              rows="2"
              placeholder="Add any verification notes..."
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800 flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>Important:</strong> No invoice will be paid without original POD being presented/delivered at accounts department.
              </span>
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => handleVerify('rejected')}
              disabled={processing}
              className="px-6 py-2 border border-red-500 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <FaTimes size={14} /> Reject
            </button>
            <button
              onClick={() => handleVerify('verified')}
              disabled={processing}
              className="px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#00A86B' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
            >
              <FaCheckCircle size={14} />
              {processing ? 'Processing...' : '✅ Verify & Accept'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminVerifyUpload;
